'use client';

import { styled } from 'styled-components';
import Link from 'next/link';
import type { IconNameType } from '@wistia/ui';
import { Icon } from '@wistia/ui';
import cs from 'classnames';
import type { CardColor } from '@types';

const BOX_SHADOW_HOVER_SHAPE = '0 0.625em 1.5em 0.625em';
const BOX_SHADOW_ACTIVE_SHAPE = '0 0.313em 0.75em 0.625em';

type IconCardProps = {
  /**
   * Color scheme of the card
   */
  colorScheme?: Exclude<CardColor, 'lightBlue'>; // Grabbing all card colors except lightBlue, which this component doesn't use!
  /**
   * Title text for the card
   */
  heading: string;
  /**
   * Body text for the card
   */
  description: string;
  /**
   * Icon to use for the card
   */
  icon: IconNameType;
  /**
   * Optional URL for the card
   */
  href?: string;
  /**
   * What the button/link should say.
   */
  ctaText?: string;
};

const IconTextLink = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.bodySm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.link};
  margin: 0;

  ${({ theme }) => theme.mq.minWidth.large} {
    font-size: ${({ theme }) => theme.typography.fontSize.desktop.bodySm};
  }
`;

const IconCardContainer = styled.div`
  border-radius: ${({ theme }) => theme.radius.sm};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing['1.5']};
  padding: ${({ theme }) => theme.spacing['2']};

  &.white {
    background-color: ${({ theme }) => theme.colors.white};
    color: ${({ theme }) => theme.colors.blue800};

    .IconCardContainer--Icon,
    ${IconTextLink} {
      color: ${({ theme }) => theme.colors.blue500};
    }

    &.IconCardContainer--Link {
      &:hover {
        box-shadow: ${({ theme }) =>
          `${BOX_SHADOW_HOVER_SHAPE} ${theme.colors.dropShadowDarkBlue}`};
      }

      &.active {
        box-shadow: ${({ theme }) =>
          `${BOX_SHADOW_ACTIVE_SHAPE} ${theme.colors.dropShadowDarkBlue}`};
      }
    }
  }

  &.darkBlue {
    background-color: ${({ theme }) => theme.colors.blue800};
    color: ${({ theme }) => theme.colors.white};

    .IconCardContainer--Icon {
      color: ${({ theme }) => theme.colors.blue400};
    }

    &.IconCardContainer--Link {
      &:hover {
        box-shadow: ${({ theme }) =>
          `${BOX_SHADOW_HOVER_SHAPE} ${theme.colors.dropShadowDarkBlue}`};
      }

      &.active {
        box-shadow: ${({ theme }) =>
          `${BOX_SHADOW_ACTIVE_SHAPE} ${theme.colors.dropShadowDarkBlue}`};
      }
    }
  }

  &.blue {
    background-color: ${({ theme }) => theme.colors.blue200};
    color: ${({ theme }) => theme.colors.blue800};

    &.IconCardContainer--Link {
      &:hover {
        box-shadow: ${({ theme }) =>
          `${BOX_SHADOW_HOVER_SHAPE} ${theme.colors.dropShadowDarkBlue}`};
      }

      &.active {
        box-shadow: ${({ theme }) =>
          `${BOX_SHADOW_ACTIVE_SHAPE} ${theme.colors.dropShadowDarkBlue}`};
      }
    }
  }

  &.purple {
    background-color: ${({ theme }) => theme.colors.purple300};
    color: ${({ theme }) => theme.colors.purple800};

    &.IconCardContainer--Link {
      &:hover {
        box-shadow: ${({ theme }) =>
          `${BOX_SHADOW_HOVER_SHAPE} ${theme.colors.dropShadowPurple}`};
      }

      &.active {
        box-shadow: ${({ theme }) =>
          `${BOX_SHADOW_ACTIVE_SHAPE} ${theme.colors.dropShadowPurple}`};
      }
    }
  }

  &.pink {
    background-color: ${({ theme }) => theme.colors.pink300};
    color: ${({ theme }) => theme.colors.pink800};

    &.IconCardContainer--Link {
      &:hover {
        box-shadow: ${({ theme }) =>
          `${BOX_SHADOW_HOVER_SHAPE} ${theme.colors.dropShadowPink}`};
      }

      &.active {
        box-shadow: ${({ theme }) =>
          `${BOX_SHADOW_ACTIVE_SHAPE} ${theme.colors.dropShadowPink}`};
      }
    }
  }

  &.orange {
    background-color: ${({ theme }) => theme.colors.orange200};
    color: ${({ theme }) => theme.colors.orange800};

    &.IconCardContainer--Link {
      &:hover {
        box-shadow: ${({ theme }) =>
          `${BOX_SHADOW_HOVER_SHAPE} ${theme.colors.dropShadowOrange}`};
      }

      &.active {
        box-shadow: ${({ theme }) =>
          `${BOX_SHADOW_ACTIVE_SHAPE} ${theme.colors.dropShadowOrange}`};
      }
    }
  }

  &.yellow {
    background-color: ${({ theme }) => theme.colors.yellow200};
    color: ${({ theme }) => theme.colors.yellow800};

    &.IconCardContainer--Link {
      &:hover {
        box-shadow: ${({ theme }) =>
          `${BOX_SHADOW_HOVER_SHAPE} ${theme.colors.dropShadowYellow}`};
      }

      &.active {
        box-shadow: ${({ theme }) =>
          `${BOX_SHADOW_ACTIVE_SHAPE} ${theme.colors.dropShadowYellow}`};
      }
    }
  }

  &.green {
    background-color: ${({ theme }) => theme.colors.green300};
    color: ${({ theme }) => theme.colors.green800};

    &.IconCardContainer--Link {
      &:hover {
        box-shadow: ${({ theme }) =>
          `${BOX_SHADOW_HOVER_SHAPE} ${theme.colors.dropShadowGreen}`};
      }

      &.active {
        box-shadow: ${({ theme }) =>
          `${BOX_SHADOW_ACTIVE_SHAPE} ${theme.colors.dropShadowGreen}`};
      }
    }
  }
`;

const IconCardHeading = styled.h4`
  margin: 0;
  padding-bottom: ${({ theme }) => theme.spacing['0.75']};
`;

const IconCardDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.bodySm};
  margin: 0;

  ${({ theme }) => theme.mq.minWidth.large} {
    font-size: ${({ theme }) => theme.typography.fontSize.desktop.bodySm};
  }
`;

const IconCard = ({
  colorScheme = 'blue',
  description,
  icon,
  ctaText = 'Learn more',
  href,
  heading,
}: IconCardProps) => {
  return href ? (
    <IconCardContainer
      as={Link}
      className={cs(colorScheme, 'IconCardContainer--Link')}
      href={href}
    >
      <Icon className="IconCardContainer--Icon" size="xl" type={icon} />
      <div>
        <IconCardHeading>{heading}</IconCardHeading>
        <IconCardDescription>{description}</IconCardDescription>
      </div>
      <IconTextLink>{ctaText} →</IconTextLink>
    </IconCardContainer>
  ) : (
    <IconCardContainer className={cs(colorScheme)}>
      <Icon className="IconCardContainer--Icon" size="xl" type={icon} />
      <div>
        <IconCardHeading>{heading}</IconCardHeading>
        <IconCardDescription>{description}</IconCardDescription>
      </div>
    </IconCardContainer>
  );
};

export default IconCard;
